/* =============================================================================
   VIEWS / ADMIN / ARCHIVE-ZBONS — Z-Bons-Sub-Section (S68 REF-2)
   =============================================================================
   Extrahiert aus archive.js. Z-Bon-Archiv = Tagesabschluss-Belege seit Phase 29.
   Quelle: Supabase `zbon_archive` (Migration 70) via App.storage.zbonArchive.
   GoBD-Aufbewahrungspflicht 10 Jahre §147 AO + §146 AO Tagesabschluss.

   Schema:
     { zBonNumber, date, count, countCorrections, countCancelled, countNoShow,
       grossCents, netCents, tax7Cents, tax19Cents, byMethod, invoiceFirst,
       invoiceLast, archivedAt, sentAt, sentTo, printedAt, html }

   S237 T1: Sub-Tab wieder an archive.js angeschlossen (war seit S182 Cut 6
   toter Code, obwohl das Z-Bon-Beleg-Modal den Pfad „Auswertung → Archiv →
   Z-Bons" bereits verspricht). Dazu ergänzt: eigener Lazy-Loader, eigene
   Filter-Leiste (Datum + Suche, KEIN Status-Select) und die Zeilen-Aktionen
   „📄 PDF" + „✉" — beide greifen auf die vorhandenen day-close-Pfade zu
   (zBonActionPdf / resendArchivedZBonEmail, window-gebridged in day-close.js).

   Exports:
     - loadZBons               Lazy-Hydrate des Z-Bon-Archiv-Caches (S237)
     - renderZBonsFilters      Section-spezifische Filter-Leiste (S237)
     - getFilteredZBons        Filter+Sort
     - zBonStatusBadges        Status-Badge-Renderer
     - renderZBonsArchive      Sub-Section-Renderer
     - exportArchiveZBonsCSV   CSV-Exporter
============================================================================ */

import { htmlEscape, fmtDate, showToast } from '../../utils/helpers.js';
import { _state } from './archive-state.js';
import { _renderImmutabilityNote, downloadCsv } from './archive-helpers.js';

/**
 * S237 T1 — Z-Bon-Archiv-Cache beim Öffnen des Z-Bons-Tabs (lazy) auffrischen,
 * exakt analog `loadBons()` (archive-bons.js). Der Cache wird schon am Boot
 * hydratisiert (bootstrap.js), ein Refresh beim Tab-Öffnen zieht Z-Bons anderer
 * Geräte nach, die seit dem Boot dazukamen (Multi-Device). getFilteredZBons
 * liest synchron aus dem Cache → wir rendern sofort mit dem vorhandenen Stand
 * und ersetzen nach dem Refresh non-destruktiv nur die Ergebnis-Liste.
 */
export async function loadZBons() {
  _state.zbonsLoading = true;
  try {
    const za = window.App && window.App.storage && window.App.storage.zbonArchive;
    if (za && typeof za.hydrate === 'function') await za.hydrate();
    _state.zbonsLoaded = true;
  } catch (e) {
    console.error('[archive] loadZBons failed:', e);
  } finally {
    _state.zbonsLoading = false;
    if (_state.section === 'zbons' && typeof window.renderResultsOnly === 'function') {
      window.renderResultsOnly();
    }
  }
}

/** Filter-Leiste der Z-Bons-Sektion — Datum von/bis + Suche, KEIN Status-Select
 *  (der Z-Bon-„Status" ist ein Versand-/Druck-Zustand, kein Filter-Kriterium). */
export function renderZBonsFilters() {
  return `
    <div class="archive-filters">
      <div class="archive-filter-row">
        <label>
          <span>Zeitraum von</span>
          <input type="date" value="${_state.dateFrom}"
                 onchange="setArchiveFilter('dateFrom', this.value)">
        </label>
        <label>
          <span>bis</span>
          <input type="date" value="${_state.dateTo}"
                 onchange="setArchiveFilter('dateTo', this.value)">
        </label>
        <label class="archive-filter-search">
          <span>Suche</span>
          <input type="search" placeholder="Z-Bon-Nr., Datum…"
                 value="${htmlEscape(_state.search)}"
                 oninput="setArchiveFilter('search', this.value)">
        </label>
      </div>
    </div>
  `;
}

/**
 * S237 T1 — Such-Heuhaufen für ein ISO-Datum: ISO + deutsche Schreibweisen,
 * damit „23.08" / „23.08.2026" / „23.08.26" genauso treffen wie „2026-08-23".
 * Bewusst ohne `toLocaleDateString` (deterministisch, TZ-/Locale-unabhängig).
 * Pure — Test-Hook.
 */
export function _zbonDateHaystack(dateIso) {
  const iso = String(dateIso || '');
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso.toLowerCase();
  const [, y, mo, d] = m;
  return `${iso} ${d}.${mo}.${y} ${d}.${mo}.${y.slice(2)}`.toLowerCase();
}

export function getFilteredZBons() {
  const loader = (typeof window.loadZBonArchive === 'function') ? window.loadZBonArchive : null;
  if (!loader) return [];
  const all = loader();
  const list = Object.values(all).filter(z => z && z.date);
  const q = _state.search.trim().toLowerCase();
  const from = _state.dateFrom;
  const to   = _state.dateTo;

  let filtered = list.filter(z => {
    const d = z.date || (z.archivedAt || '').slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

  if (q) {
    filtered = filtered.filter(z =>
      (z.zBonNumber && String(z.zBonNumber).toLowerCase().includes(q)) ||
      // S237 T1: zusätzlich über das formatierte Datum (dd.mm.yyyy / dd.mm.yy).
      (z.date && _zbonDateHaystack(z.date).includes(q))
    );
  }

  filtered.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  return filtered;
}

export function zBonStatusBadges(z) {
  const badges = [];
  if (z.printedAt) badges.push('<span class="badge badge-completed">🖨️ gedruckt</span>');
  if (z.sentAt)    badges.push('<span class="badge badge-confirmed">✉ versendet</span>');
  if (z.archivedAt)badges.push('<span class="badge badge-pending">🗄️ archiviert</span>');
  if (badges.length === 0) badges.push('<span style="opacity:.6;">offen</span>');
  return badges.join(' ');
}

export function renderZBonsArchive() {
  const list = getFilteredZBons();
  if (list.length === 0) {
    return `
      <div class="empty">
        <div class="empty-emoji">📋</div>
        Noch keine Z-Bons im Archiv.
        <p style="font-size:.85rem; color: var(--ink-muted); margin-top: 8px;">
          Im Tagesabschluss-Tab «Z-Bon ausgeben» klicken und «Archivieren» wählen — der Z-Bon erscheint dann hier.
        </p>
      </div>
    `;
  }

  const cToE = (cents) => {
    const v = (Number(cents) || 0) / 100;
    return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const pgZ = (window.PaginatedList && window.PaginatedList.paginate)
    ? window.PaginatedList.paginate({
        items: list, containerId: 'archive-zbons-pager',
        initial: 10, step: 10, rendererFn: 'renderArchive'
      })
    : { slice: list.slice(0, 10), moreButtonHtml: '', visible: Math.min(10, list.length), total: list.length };

  const rowsHtml = pgZ.slice.map(z => {
    const key = htmlEscape(z.date);
    return `
      <tr class="archive-row" onclick="viewArchivedZBon('${key}')"
          title="Z-Bon-Details öffnen">
        <td><strong>${fmtDate(z.date)}</strong></td>
        <td><span style="font-variant-numeric: tabular-nums;">${htmlEscape(z.zBonNumber || '—')}</span></td>
        <td style="text-align:right;">${cToE(z.grossCents)}</td>
        <td style="text-align:right;">${cToE(z.netCents)}</td>
        <td style="text-align:right;">${cToE(z.tax7Cents)}</td>
        <td style="text-align:right;">${cToE(z.tax19Cents)}</td>
        <td style="text-align:center;">${z.count || 0}</td>
        <td>${zBonStatusBadges(z)}</td>
        <td class="archive-bon-actions">
          <button class="btn btn-ghost btn-sm" title="Z-Bon als PDF öffnen"
                  onclick="event.stopPropagation(); zBonActionPdf('${key}')">📄 PDF</button>
          <button class="btn btn-ghost btn-sm" title="Z-Bon erneut an den Steuerberater versenden"
                  onclick="event.stopPropagation(); resendArchivedZBonEmail('${key}')">✉</button>
        </td>
      </tr>
    `;
  }).join('');

  return `
    <div class="archive-meta">
      <span>
        ${list.length} Z-Bon${list.length === 1 ? '' : 's'}
        · zeige ${pgZ.visible} von ${pgZ.total}
      </span>
      <button class="btn-archive-reload" onclick="exportArchiveZBonsCSV()"
              title="Z-Bon-Archiv als CSV herunterladen">
        📥 CSV-Export
      </button>
    </div>
    <div class="archive-table-wrap">
      <table class="archive-table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Z-Nr.</th>
            <th style="text-align:right;">Brutto</th>
            <th style="text-align:right;">Netto</th>
            <th style="text-align:right;">MwSt 7 %</th>
            <th style="text-align:right;">MwSt 19 %</th>
            <th style="text-align:center;">Best.</th>
            <th>Status</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </div>
    ${pgZ.moreButtonHtml}
    ${_renderImmutabilityNote('§146 AO + GoBD · Tagesabschluss, append-only')}
  `;
}

export function exportArchiveZBonsCSV() {
  const list = getFilteredZBons();
  if (list.length === 0) { showToast('Keine Z-Bons im Filter — nichts zu exportieren', 'info'); return; }

  const cToE = (cents) => ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
  const header = [
    'Datum', 'Z-Nr.', 'Brutto (EUR)', 'Netto (EUR)',
    'MwSt 7% (EUR)', 'MwSt 19% (EUR)',
    'Anzahl Bestellungen', 'Korrekturen', 'Stornos', 'No-Shows',
    'Bar (EUR)', 'Karte (EUR)', 'Stripe (EUR)', 'Unbezahlt (EUR)',
    'Rg.-Nr. von', 'Rg.-Nr. bis',
    'Gedruckt am', 'Versendet am', 'Versendet an', 'Archiviert am'
  ];
  const rows = [header];
  for (const z of list) {
    const m = z.byMethod || {};
    rows.push([
      z.date || '', z.zBonNumber || '',
      cToE(z.grossCents), cToE(z.netCents),
      cToE(z.tax7Cents), cToE(z.tax19Cents),
      z.count || 0, z.countCorrections || 0, z.countCancelled || 0, z.countNoShow || 0,
      cToE(m.cash), cToE(m.card), cToE(m.stripe), cToE(m.unpaid),
      z.invoiceFirst || '', z.invoiceLast || '',
      z.printedAt || '', z.sentAt || '', z.sentTo || '', z.archivedAt || ''
    ]);
  }
  downloadCsv(rows, `zbons-archiv-${_state.dateFrom}_bis_${_state.dateTo}.csv`);
  showToast(`${list.length} Z-Bon${list.length === 1 ? '' : 's'} exportiert`, 'success');
}
